/**
 * Pure client-side state machine and routing helpers. No DOM, no fetch: these
 * functions take server response shapes (or a location.pathname) and return
 * which screen to render, so the whole routing decision is unit-testable
 * without a DOM shim (test/client/app-state.spec.tsx).
 *
 * The rule this file exists to enforce: no other client code branches on
 * `auth.state` directly. Every screen decision goes through
 * screenForAuthState so a hard reload always lands on the right screen (the
 * boot-time session-restore fix class the brief calls out).
 */
import type { PortalRole } from '../shared/roles.js';

/** ⭐ T2075 M2 (plan D1) — `role` and `groupLabel` arrive on the `needs_pin`
 *  and `active` arms of `GET /api/auth/state`, read live off the session on
 *  every poll; the tab strip renders from `role` in T2075 M4. `groupLabel` is
 *  null for reception / admin and for a pre-v13 account. ⛔ NOT on
 *  `RosterEntry` below — the locked roster is a device-cookie surface. */
export type NurseInfo = { id: number; displayName: string; role: PortalRole; groupLabel: string | null };
export type HomeInfo = { id: number; name: string };
/** ⛔ NO `hasPin` — board 786. The server stopped serving it on the locked
 *  roster because it disclosed every nurse's PIN-enrolment state to anyone
 *  holding the device cookie. Nothing in the client ever read it (the lock
 *  screen renders `displayName` only), so leaving it declared would have been a
 *  type that lies about the wire. Do not re-add it. */
export type RosterEntry = { id: number; displayName: string };

export type AuthState =
  | { state: 'no_device' }
  | { state: 'locked'; roster: RosterEntry[]; home: HomeInfo | null }
  | { state: 'needs_pin'; nurse: NurseInfo }
  | { state: 'active'; nurse: NurseInfo; home: HomeInfo | null };

export type Screen = 'enrol' | 'picker' | 'pin-setup' | 'active';

/** Maps GET /api/auth/state's `state` field to the screen to render. */
export function screenForAuthState(auth: Pick<AuthState, 'state'>): Screen {
  switch (auth.state) {
    case 'no_device':
      return 'enrol';
    case 'locked':
      return 'picker';
    case 'needs_pin':
      return 'pin-setup';
    case 'active':
      return 'active';
    default: {
      // Exhaustiveness guard: an unrecognised state string (a future server
      // addition the client hasn't learned yet) falls back to enrol rather
      // than crashing on a dead screen.
      const _exhaustive: never = auth.state;
      void _exhaustive;
      return 'enrol';
    }
  }
}

const KNOWN_STATES = ['no_device', 'locked', 'needs_pin', 'active'];

/** Narrows an arbitrary parsed JSON body to AuthState. Used right after
 *  GET /api/auth/state so a malformed/network-broken body degrades to a
 *  safe "treat as signed out" read rather than throwing mid-render. */
export function isAuthState(value: unknown): value is AuthState {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as { state?: unknown }).state === 'string' &&
    KNOWN_STATES.includes((value as { state: string }).state)
  );
}

// ---------------------------------------------------------------------------
// Path routing (no router library; checked once at boot per the brief)
// ---------------------------------------------------------------------------

export type Route = 'nurse-app' | 'ops' | 'dev-mailbox';

export function routeForPath(pathname: string): Route {
  if (pathname === '/ops') return 'ops';
  if (pathname === '/dev/mailbox') return 'dev-mailbox';
  return 'nurse-app';
}

// ---------------------------------------------------------------------------
// Active-tab (New request / My requests / My residents) within the 'active'
// screen
// ---------------------------------------------------------------------------

/** ⛔ THE DEFAULT STAYS `new-request` (app.tsx). T1717 M3 added the third tab;
 *  a nurse's reason for opening the portal is still to send a request, and the
 *  roster is maintenance she goes looking for.
 *  ⭐ T2075 M4 (plan D13, A8) — `home-requests` and `staff` join the union; which
 *  of them a session is offered is `tabsForRole` below, never a branch in
 *  `app.tsx`. */
export type ActiveTab = 'new-request' | 'my-requests' | 'my-residents' | 'home-requests' | 'staff';

/**
 * ⭐ T2075 M4 (plan A8, D13) — THE TAB STRIP PER ROLE, as a pure list so the
 * per-role matrix is unit-testable without a DOM (`app-state.spec.tsx`): a
 * `group` account keeps the three base tabs; `reception` and `admin` gain
 * `Requests` (the home-wide list); ⭐ T2075 M5 — `admin` gains `Staff` (the
 * roster and the sign-in accounts), the fifth tab, offered only now that the
 * screen exists (a tab that leads nowhere fails the Helen-test). The base
 * three come first so the default tab is still the first one.
 */
export function tabsForRole(role: PortalRole): ActiveTab[] {
  const base: ActiveTab[] = ['new-request', 'my-requests', 'my-residents'];
  if (role === 'reception' || role === 'admin') base.push('home-requests');
  if (role === 'admin') base.push('staff');
  return base;
}

/**
 * ⭐⭐ T2173 rework 1 — THE TAB AN IDENTITY LANDS ON, and it exists because the
 * shared device kept the PREVIOUS person's tab.
 *
 * ⛔ THE BUG IT CLOSES IS A PRIVILEGE ONE, not a tidiness one. `tab` is shell
 * state, not session state, so signing a different person in on the same device
 * left it where the last person was. An admin on `staff` who locked, followed by
 * a group nurse signing in, put that nurse on a screen her role cannot load:
 * Captain's live drive measured THREE 403s in a row (`/api/admin/staff`,
 * `/api/admin/accounts`, `/api/home/requests`) behind a "Could not load…". The
 * strip was right all along — `tabsForRole` never offered her the tab — so the
 * defect was that nothing moved her OFF it.
 *
 * ⛔ THE RETURN MUST BE A MEMBER OF `tabsForRole(role)`, always. A default outside
 * that set would reproduce the same forbidden-screen state by a different route,
 * which is why `state.spec.ts` asserts membership for every role rather than
 * asserting the three literals.
 *
 * ⚠️ RECEPTION IS THE ONE EXCEPTION and it is a job description, not a
 * preference: reception books on behalf of the home and reads the home-wide
 * list, so `All requests` is where the work is. A group nurse and an admin both
 * start where every session has always started.
 */
export function defaultTabForRole(role: PortalRole): ActiveTab {
  return role === 'reception' ? 'home-requests' : 'new-request';
}

// ---------------------------------------------------------------------------
// ⛔ REMOVED (board 1050): `isPinResetResponse` and `attemptsLeftFrom`.
//
// They read a `409 pin_reset` and a `401 invalid_pin` + `attemptsLeft` off
// pin-login. Board 786/1049 unified that route onto ONE response — an identical
// `401 {error:'invalid_pin'}` for wrong-PIN, no-PIN and PIN-just-destroyed alike
// — so both functions became permanently unreachable: the first could never be
// true, the second could never be non-null.
//
// ⛔ DO NOT REINTRODUCE EITHER, and do not add a replacement that inspects the
// response BODY. Distinguishing those cases client-side requires the server to
// disclose them, and that disclosure IS the oracle 786 closed. Picker now
// branches on HTTP STATUS ALONE and offers recovery unconditionally instead.
// See screens/Picker.tsx and server/auth-routes.ts's uniform-401 header.
// ---------------------------------------------------------------------------
